// Pure logic behind the /admin Lexicon "+ Term" form and inline row editing.
// Kept separate from AdminLexiconTab.tsx because the web suite runs in
// `environment: "node"` (see lexiconPropagation.ts, lexiconImportMessages.ts
// for the same pattern) — this is where the behaviour that can actually be
// wrong (payload shaping, validation, the managed-row guard) gets tested.

import type { LexiconRow } from "./AdminLexiconTab";

export const OCCURRENCE_WEIGHTS: readonly number[] = [1, 2, 3];

/**
 * The allowed `category`/`entry_kind` values, as served by
 * `GET /api/admin/lexicon/category-counts` (fields `categories` /
 * `entry_kinds`). That endpoint sorts and re-exports lexicon_import.py's
 * own CATEGORIES/ENTRY_KINDS — the mirror of the database's
 * medical_lexicon_entry_kind enum and
 * central_medical_lexicon_category_check constraint — so the frontend never
 * keeps a second hardcoded copy of a clinical constraint list. An empty
 * array here means "not loaded yet", not "nothing is allowed".
 */
export type LexiconAllowedValues = {
  categories: readonly string[];
  entryKinds: readonly string[];
};

/** Controlled-input state for both the create form and an inline edit row. */
export type LexiconFormState = {
  term: string;
  discipline: string;
  entryKind: string;
  subgroup: string;
  occurrenceWeight: string;
  category: string;
  usageContexts: string;
  canonicalTerm: string;
  phoneticAlternative1: string;
  phoneticAlternative2: string;
};

export function emptyLexiconFormState(): LexiconFormState {
  return {
    term: "",
    discipline: "cardiologie",
    entryKind: "keyword",
    subgroup: "",
    occurrenceWeight: "1",
    category: "",
    usageContexts: "",
    canonicalTerm: "",
    phoneticAlternative1: "",
    phoneticAlternative2: "",
  };
}

/** Seed an inline-edit form from the row currently on screen. */
export function lexiconFormStateFromRow(row: LexiconRow): LexiconFormState {
  return {
    term: row.term,
    discipline: row.discipline,
    entryKind: row.entry_kind,
    subgroup: row.subgroup,
    occurrenceWeight: String(row.occurrence_weight),
    category: row.category ?? "",
    usageContexts: row.usage_contexts.join(", "),
    canonicalTerm: row.canonical_term ?? "",
    phoneticAlternative1: row.phonetic_alternative_1 ?? "",
    phoneticAlternative2: row.phonetic_alternative_2 ?? "",
  };
}

/**
 * A comma-separated `usage_contexts` input, split into the string list the
 * API expects. Empty entries (from "a,,b" or trailing commas) are dropped
 * rather than sent as blank strings.
 */
export function parseUsageContexts(input: string): string[] {
  return input
    .split(",")
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

/**
 * `occurrence_weight` coerced to a number, or null when it is not one of
 * the three values the CHECK constraint allows. Mirrors the server's
 * `_check_weight`, so a bad value is caught before the request is sent
 * rather than only after a 422 round-trip.
 */
export function coerceOccurrenceWeight(input: string): number | null {
  const weight = Number(input);
  if (!Number.isInteger(weight)) return null;
  if (!OCCURRENCE_WEIGHTS.includes(weight)) return null;
  return weight;
}

/** The request body for `POST /api/admin/lexicon/rows`. */
export type LexiconCreatePayload = {
  term: string;
  discipline?: string;
  entry_kind?: string;
  subgroup?: string;
  occurrence_weight?: number;
  category?: string;
  usage_contexts?: string[];
  canonical_term?: string;
  phonetic_alternative_1?: string;
  phonetic_alternative_2?: string;
};

function trimmedOrUndefined(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Validate a create-form state and build the POST body. Client-side
 * validation is a convenience — the server is the actual authority (see
 * `_check_weight`/`_check_category`/`_check_entry_kind` in
 * admin_lexicon.py) — so this checks the same three constraints against
 * `allowed` (the categories/entry_kinds the server itself served), and
 * nothing else. Empty optional fields are dropped rather than sent as "",
 * so the server's own defaults (e.g. discipline "cardiologie") stay in
 * control when a field is genuinely left blank.
 */
export function buildCreatePayload(
  state: LexiconFormState,
  allowed: LexiconAllowedValues,
): { payload: LexiconCreatePayload; errors: [] } | { payload: null; errors: string[] } {
  const errors: string[] = [];

  const term = state.term.trim();
  if (!term) errors.push("Term is verplicht.");

  const weight = coerceOccurrenceWeight(state.occurrenceWeight);
  if (weight === null) errors.push("Gewicht moet 1, 2 of 3 zijn.");

  if (state.entryKind && !allowed.entryKinds.includes(state.entryKind)) {
    errors.push(`Soort "${state.entryKind}" is niet geldig.`);
  }
  if (state.category && !allowed.categories.includes(state.category)) {
    errors.push(`Categorie "${state.category}" is niet geldig.`);
  }

  if (errors.length > 0 || weight === null) return { payload: null, errors };

  const payload: LexiconCreatePayload = { term, occurrence_weight: weight };
  const discipline = trimmedOrUndefined(state.discipline);
  if (discipline) payload.discipline = discipline;
  if (state.entryKind) payload.entry_kind = state.entryKind;
  const subgroup = trimmedOrUndefined(state.subgroup);
  if (subgroup) payload.subgroup = subgroup;
  if (state.category) payload.category = state.category;
  const usageContexts = parseUsageContexts(state.usageContexts);
  if (usageContexts.length > 0) payload.usage_contexts = usageContexts;
  const canonicalTerm = trimmedOrUndefined(state.canonicalTerm);
  if (canonicalTerm) payload.canonical_term = canonicalTerm;
  const phonetic1 = trimmedOrUndefined(state.phoneticAlternative1);
  if (phonetic1) payload.phonetic_alternative_1 = phonetic1;
  const phonetic2 = trimmedOrUndefined(state.phoneticAlternative2);
  if (phonetic2) payload.phonetic_alternative_2 = phonetic2;

  return { payload, errors: [] };
}

/** The request body for `PATCH /api/admin/lexicon/rows/{id}`. */
export type LexiconPatchPayload = Partial<{
  term: string;
  discipline: string;
  subgroup: string;
  occurrence_weight: number;
  category: string | null;
  usage_contexts: string[];
  canonical_term: string | null;
  phonetic_alternative_1: string | null;
  phonetic_alternative_2: string | null;
}>;

function sameStringArray(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

/**
 * Validate an inline-edit form against the row it started from and build
 * the PATCH body — only the fields that actually changed, so a save that
 * touched one field never re-sends the other nine. `entry_kind` is not
 * editable (the API does not accept it in PATCH), matching
 * lexiconFormStateFromRow only reading it for display.
 */
export function buildPatchPayload(
  original: LexiconRow,
  state: LexiconFormState,
  allowed: LexiconAllowedValues,
): { payload: LexiconPatchPayload; errors: [] } | { payload: null; errors: string[] } {
  const errors: string[] = [];

  const term = state.term.trim();
  if (!term) errors.push("Term is verplicht.");

  const weight = coerceOccurrenceWeight(state.occurrenceWeight);
  if (weight === null) errors.push("Gewicht moet 1, 2 of 3 zijn.");

  if (state.category && !allowed.categories.includes(state.category)) {
    errors.push(`Categorie "${state.category}" is niet geldig.`);
  }

  if (errors.length > 0 || weight === null) return { payload: null, errors };

  const discipline = state.discipline.trim();
  const subgroup = state.subgroup.trim();
  const category = state.category || null;
  const usageContexts = parseUsageContexts(state.usageContexts);
  const canonicalTerm = trimmedOrUndefined(state.canonicalTerm) ?? null;
  const phonetic1 = trimmedOrUndefined(state.phoneticAlternative1) ?? null;
  const phonetic2 = trimmedOrUndefined(state.phoneticAlternative2) ?? null;

  const payload: LexiconPatchPayload = {};
  if (term !== original.term) payload.term = term;
  if (discipline !== original.discipline) payload.discipline = discipline;
  if (subgroup !== original.subgroup) payload.subgroup = subgroup;
  if (weight !== original.occurrence_weight) payload.occurrence_weight = weight;
  if (category !== (original.category ?? null)) payload.category = category;
  if (!sameStringArray(usageContexts, original.usage_contexts)) {
    payload.usage_contexts = usageContexts;
  }
  if (canonicalTerm !== (original.canonical_term ?? null)) payload.canonical_term = canonicalTerm;
  if (phonetic1 !== (original.phonetic_alternative_1 ?? null)) {
    payload.phonetic_alternative_1 = phonetic1;
  }
  if (phonetic2 !== (original.phonetic_alternative_2 ?? null)) {
    payload.phonetic_alternative_2 = phonetic2;
  }

  if (Object.keys(payload).length === 0) {
    return { payload: null, errors: ["Er zijn geen wijzigingen om op te slaan."] };
  }

  return { payload, errors: [] };
}

/**
 * Whether a row may offer edit/deactivate affordances at all. A `managed`
 * row is rebuilt by import_medical_lexicon.py and the API refuses any
 * write to it with 409 — the UI must not invite an attempt the server will
 * only reject. Mirrors the `row.managed ? null : ...` guard already used
 * for the deactivate button.
 */
export function isLexiconRowEditable(row: LexiconRow): boolean {
  return !row.managed;
}
